import React, { useEffect } from 'react'
import { Select, TextField } from '@boltenergy-be/design-system'
import { useFormContext, useWatch } from 'react-hook-form'
import { AddressFields, AddressProps } from 'components/inputs/address/types'
import { useTranslation } from 'next-i18next/pages'
import { POSTAL_CODE } from 'constants/regex'
import { updateInput } from 'store/customer/slice'
import { InputGroupKeys, MeterDetailsFieldKeys, SimulationFieldKeys } from 'store/customer/enums'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { getFieldError, getFieldName } from 'components/inputs/utils'
import styles from './PostalCodeAndTown.module.scss'
import { COUNTER_TYPES, Region, REGIONS } from '@boltenergy-be/shared'
import { useGetTownsQuery } from 'store/api/bolt-api'
import { Towns } from 'types/towns'

/**
 * Address component
 * @remarks: This component is used to render the postal code & town fields in a form. The inputs required for the RHF are:
 * - postalCode
 * - townName
 * - townCode
 *
 * @param {AddressProps} param0
 */
const PostalCodeAndTown = ({
  grid,
  hasRegionField,
  hiddenLabel,
  hideTownSelectField,
  parentDotPath,
  required = true,
  setFetchingTowns,
  hideErrorMessage
}: AddressProps) => {
  // Redux store
  const counterType = useStoreSelector((store) => store.customer.inputs[InputGroupKeys.METER_DETAILS][MeterDetailsFieldKeys.COUNTER_TYPE])
  const dispatch = useStoreDispatch()

  // React Hook Form
  const {
    formState: { errors },
    control,
    getValues,
    register,
    setError,
    setValue,
    watch
  } = useFormContext()
  const postalCodeField = getFieldName(AddressFields.POSTAL_CODE, parentDotPath)
  const townNameField = getFieldName(AddressFields.TOWN_NAME, parentDotPath)
  const townCodeField = getFieldName(AddressFields.TOWN_CODE, parentDotPath)
  const watchPostalCode: string = useWatch({ control, name: postalCodeField })
  const watchTownName = watch(townNameField)

  // Redux query
  const {
    currentData: townsData,
    isError,
    isFetching
  } = useGetTownsQuery(watchPostalCode, { skip: !POSTAL_CODE.test(watchPostalCode ?? '') })
  const towns: Towns = townsData?.towns ?? []

  // i18n
  const { t } = useTranslation(['commonFormFields', 'validation', 'common'])

  /**
   * Syncs the fetched towns with the form & the store
   */
  useEffect(() => {
    if (!townsData) return

    const { towns: fetchedTowns, region } = townsData

    // Keep the town that was already selected when it still exists for this postal code, so a previously
    // chosen town survives a remount of the form. Fall back to the first town otherwise.
    const currentTownName = getValues(townNameField)
    const selectedTown = fetchedTowns.find((town) => town.townName === currentTownName) ?? fetchedTowns[0]

    setValue(townCodeField, selectedTown?.townCode || 0)
    setValue(townNameField, selectedTown?.townName || '')

    if (hasRegionField) {
      setValue(getFieldName(AddressFields.REGION, parentDotPath), region)
    }

    updateStoreDataBasedOnRegion(region)
  }, [townsData])

  /**
   * Shows an error on the postal code field when the towns could not be fetched
   */
  useEffect(() => {
    if (isError) {
      setError(postalCodeField, { type: 'api', message: t('common:error') })
    }
  }, [isError])

  /**
   * Reports the fetching state to the parent, which needs it when the town select is not rendered
   */
  useEffect(() => {
    setFetchingTowns?.(isFetching)
  }, [isFetching])

  /**
   * Handles the town select change & sets the town code
   *
   * @param {string} selectedTown
   */
  const handleTownSelectChange = (selectedTown: string) => {
    const town = towns.find((town) => town.townName === selectedTown)

    setValue(townCodeField, town?.townCode || 0)
    setValue(townNameField, town?.townName || '')
  }

  /**
   * Updates some store data based on the selected region
   * @param {Region} region
   */
  const updateStoreDataBasedOnRegion = (region: Region) => {
    // Update the region in the store
    dispatch(updateInput({ group: InputGroupKeys.SIMULATION, key: SimulationFieldKeys.REGION, value: region }))

    // Set hasSolarPanels to false if Brussels & analog meter
    if (region === REGIONS.brussels && counterType === COUNTER_TYPES.analog) {
      dispatch(
        updateInput({
          group: InputGroupKeys.METER_DETAILS,
          key: MeterDetailsFieldKeys.HAS_SOLAR_PANELS,
          value: false
        })
      )
    }
  }

  return (
    <>
      <TextField
        {...register(postalCodeField, {
          pattern: { value: POSTAL_CODE, message: t('commonFormFields:postalCode.errors.invalid') },
          required: {
            value: required,
            message: t('validation:required')
          },
          validate: () => !isError || t('common:error')
        })}
        error={
          hideErrorMessage
            ? !!getFieldError(AddressFields.POSTAL_CODE, parentDotPath, errors)?.message
            : getFieldError(AddressFields.POSTAL_CODE, parentDotPath, errors)?.message
        }
        grid={grid}
        hiddenLabel={hiddenLabel}
        inputMode="numeric"
        label={t('commonFormFields:postalCode.label')}
        type="number"
        className={styles['postal-code']}
      />

      {!hideTownSelectField ? (
        <Select
          {...register(townNameField, {
            onChange: (e) => handleTownSelectChange(e.target.value),
            required: {
              value: required,
              message: t('validation:required')
            }
          })}
          disabled={towns.length === 0}
          error={
            hideErrorMessage
              ? !!getFieldError(AddressFields.TOWN_NAME, parentDotPath, errors)?.message
              : getFieldError(AddressFields.TOWN_NAME, parentDotPath, errors)?.message
          }
          grid={grid}
          hiddenLabel={hiddenLabel}
          label={t('commonFormFields:townName.label')}
          loading={isFetching}
          value={watchTownName}
        >
          <option disabled>{t('commonFormFields:select')}</option>
          {towns.map((town) => (
            <option key={`town.geoCode-${town.townCode}`} value={town.townName}>
              {town.townName}
            </option>
          ))}
        </Select>
      ) : (
        <input type="hidden" {...register(townNameField)} />
      )}

      <input type="hidden" {...register(townCodeField)} />
    </>
  )
}

export default PostalCodeAndTown
